import React, { useState, useEffect } from 'react';
import { Table, Form, Pagination, InputGroup, Button, Card } from 'react-bootstrap';
import { FaSearch, FaTrash, FaEye, FaPlus, FaEdit, FaUser, FaPhone, FaEnvelope, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import HeaderTopSection from "../../components/partials/HeaderTop";
import ClientDetailModal from "../../components/modals/client/ClientDetailModal";
import CreateClientModal from "../../components/modals/client/CreateClientModal";
import ClientService from '../../services/ClientService';
import UserService from '../../services/UserService';
import LoadingSpinner from '../../utils/LoadingSpinner';
import Banner from "../../components/partials/Banner";

const DEFAULT_AVATAR = 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/profile/avatar.jpg';

const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
};

// Hook personnalisé pour le debounce
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

/**
 * Aplati la réponse ClientResponseDTO (client + client.user imbriqué)
 * en un objet unique exploitable par le tableau existant.
 */
const mapClientDto = (client) => {
    const user = client.user || {};

    return {
        // Identifiants
        id: client.id,               // id de l'entité Client (utilisé pour delete/update client)
        userId: user.id,             // id de l'entité User sous-jacente

        // Champs "client"
        useRef: client.useRef || user.useRef || '',
        totalNumOfContracts: client.totalNumOfContracts ?? 0,
        profilePictureUrl: client.profilePhotoUrlPresigned || user.profilePictureUrl || DEFAULT_AVATAR,
        identityDocumentUrl: client.identityDocumentUrlPresigned || '',
        identityDocumentType: client.identityDocumentType || '',
        modifiedAt: client.modifiedAt || user.modifiedAt || '',

        // Champs "user" (utilisés pour affichage + statut de compte)
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        gender: user.gender || 'UNSPECIFIED',
        neighborhood: user.neighborhood || '',
        city: user.city || '',
        country: user.country || '',
        createdAt: user.createdAt || '',
        dateOfBirth: user.dateOfBirth || '',
        username: user.username || '',
        additionalInfos: user.additionalInfos || '',

        // Statut de compte (basé sur le user, comme dans la version d'origine)
        enabled: user.enabled ?? true,
        accountNonLocked: user.accountNonLocked ?? true,
        accountNonExpired: user.accountNonExpired ?? true,
        credentialsNonExpired: user.credentialsNonExpired ?? true,

        // On garde le DTO brut au cas où les modales en auraient besoin
        raw: client,
    };
};

const ClientManagementPage = () => {
    const [clients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const [error, setError] = useState(null);
    const itemsPerPage = 10;

    // Charger tous les clients une seule fois au montage
    // (getAllClients() renvoie déjà uniquement les clients, sans pagination serveur)
    useEffect(() => {
        fetchClients();
    }, []);

    // Revenir à la page 1 quand la recherche change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    const fetchClients = async () => {
        const startTime = Date.now();
        setShowLoadingSpinner(true);
        setIsLoading(true);
        setError(null);

        try {
            const response = await ClientService.getAllClients();
            const mappedClients = (response || []).map(mapClientDto);

            setClients(mappedClients);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                setIsLoading(false);
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setError(err.message || 'Erreur lors du chargement des clients');
                setShowLoadingSpinner(false);
                setIsLoading(false);
                Swal.fire('Erreur !', 'Impossible de charger les clients. Veuillez réessayer.', 'error');
            }, remainingTime);
        }
    };

    // Filtrer les clients localement basé sur le searchTerm
    const filteredClients = clients.filter(client =>
        `${client.firstName} ${client.lastName}`.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        client.phoneNumber?.includes(debouncedSearchTerm) ||
        client.useRef?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
    const currentClients = filteredClients.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Calcul des statistiques (directement sur `clients`, plus besoin d'une liste séparée)
    const clientStats = {
        total: debouncedSearchTerm ? filteredClients.length : clients.length,
        active: debouncedSearchTerm ?
            filteredClients.filter(client => client.enabled === false).length :
            clients.filter(client => client.enabled === false).length,
        inactive: debouncedSearchTerm ?
            filteredClients.filter(client => client.enabled === true).length :
            clients.filter(client => client.enabled === true).length,
        isFiltered: !!debouncedSearchTerm
    };

    const handleDelete = async (client) => {
        const result = await Swal.fire({
            title: 'Confirmer la suppression',
            html: `Êtes-vous sûr de vouloir supprimer ce client ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            const startTime = Date.now();
            setShowLoadingSpinner(true);

            try {
                // NB: on supprime le client via son id d'entité Client (pas l'id User)
                await ClientService.deleteClient(client.id);
                await fetchClients();

                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(3000 - elapsedTime, 0);

                setTimeout(() => {
                    setShowLoadingSpinner(false);
                    Swal.fire('Supprimé !', 'Le client a été supprimé avec succès.', 'success');
                }, remainingTime);

            } catch (err) {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(3000 - elapsedTime, 0);

                setTimeout(() => {
                    setShowLoadingSpinner(false);
                    Swal.fire('Erreur !', 'La suppression a échoué. Veuillez réessayer.', 'error');
                }, remainingTime);
            }
        }
    };

    const handleViewDetails = (client) => {
        setSelectedClient(client);
        setIsModalOpen(true);
    };

    const handleRowClick = (client, e) => {
        if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
            return;
        }
        handleViewDetails(client);
    };

    const handleCreateUser = async () => {
        const startTime = Date.now();
        setShowLoadingSpinner(true);

        try {
            await fetchClients();
            setIsCreateModalOpen(false);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire(
                    'Client créé !',
                    'Le nouveau client a été ajouté avec succès.',
                    'success'
                );
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire(
                    'Erreur !',
                    'La création a échoué. Veuillez réessayer.',
                    'error'
                );
            }, remainingTime);
        }
    };

    // ATTENTION: cette fonction met à jour le USER (comme dans la version d'origine),
    // pas l'entité Client. Si ClientDetailModal doit désormais modifier des champs
    // spécifiques au Client (ex: identityDocumentType), remplace par
    // ClientService.updateClient(selectedClient.id, updatedClientData).
    const handleSaveUser = async (userId, updatedUserData) => {
        const startTime = Date.now();
        setShowLoadingSpinner(true);

        try {
            await UserService.updateUser(userId, updatedUserData);
            await fetchClients();
            setIsModalOpen(false);

            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                Swal.fire(
                    'Modification réussie !',
                    'Les informations du client ont été mises à jour.',
                    'success'
                );
            }, remainingTime);

        } catch (err) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(3000 - elapsedTime, 0);

            setTimeout(() => {
                setShowLoadingSpinner(false);
                console.error('Error updating user:', err);
                Swal.fire(
                    'Erreur !',
                    err.response?.data?.message || 'La modification a échoué. Veuillez réessayer.',
                    'error'
                );
            }, remainingTime);
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Gestion des Clients"
            />

            <LoadingSpinner
                isVisible={showLoadingSpinner}
                loadingText="Chargement des clients..."
            />

            <div className="container mt-5" style={{ backgroundColor: '#fafcfd', minHeight: '80vh' }}>
                <Card style={{
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    margin: '20px'
                }}>
                    <Card.Header style={{
                        backgroundColor: '#153247',
                        color: 'white',
                        padding: '20px 30px',
                        borderBottom: 'none'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="mb-1" style={{ fontWeight: '600', fontSize: '1.8rem', color:'white' }}>
                                    <FaUser className="me-2" />
                                    Gérez l'ensemble de vos clients
                                </h2>
                                {clientStats.isFiltered && (
                                    <small className="text-warning">
                                        Affichage des résultats de recherche ({filteredClients.length} client(s) trouvé(s))
                                    </small>
                                )}
                            </div>
                        </div>
                    </Card.Header>

                    <Card.Body style={{ padding: '30px' }}>
                        {/* Section des statistiques */}
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <Card className="stat-card" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px'
                                }}>
                                    <Card.Body className="text-center">
                                        <FaUser className="stat-icon" style={{ fontSize: '2rem', marginBottom: '10px' }} />
                                        <h3 style={{ fontWeight: '700', margin: '0' }}>{clientStats.total}</h3>
                                        <p style={{ margin: '0', opacity: '0.9', color: 'white' }}>
                                            {clientStats.isFiltered ? 'Clients Trouvés' : 'Clients Totaux'}
                                        </p>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-4">
                                <Card className="stat-card" style={{
                                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px'
                                }}>
                                    <Card.Body className="text-center">
                                        <FaUserCheck className="stat-icon" style={{ fontSize: '2rem', marginBottom: '10px' }} />
                                        <h3 style={{ fontWeight: '700', margin: '0' }}>{clientStats.active}</h3>
                                        <p style={{ margin: '0', opacity: '0.9' , color: 'white'}}>
                                            {clientStats.isFiltered ? 'Actifs Trouvés' : 'Clients Actifs'}
                                        </p>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-4">
                                <Card className="stat-card" style={{
                                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa8a8 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '12px'
                                }}>
                                    <Card.Body className="text-center">
                                        <FaUserTimes className="stat-icon" style={{ fontSize: '2rem', marginBottom: '10px' }} />
                                        <h3 style={{ fontWeight: '700', margin: '0' }}>{clientStats.inactive}</h3>
                                        <p style={{ margin: '0', opacity: '0.9' , color: 'white'}}>
                                            {clientStats.isFiltered ? 'Inactifs Trouvés' : 'Clients Inactifs'}
                                        </p>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>

                        {/* Barre de recherche */}
                        <div className="mb-4">
                            <InputGroup style={{ maxWidth: '500px' }}>
                                <InputGroup.Text style={{
                                    backgroundColor: '#153247',
                                    border: 'none',
                                    color: 'white',
                                    borderTopLeftRadius: '8px',
                                    borderBottomLeftRadius: '8px'
                                }}>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher par nom, email, téléphone ou référence..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        borderLeft: 'none',
                                        borderRadius: '0 8px 8px 0',
                                        padding: '12px 15px'
                                    }}
                                />
                            </InputGroup>
                        </div>

                        {isLoading && !showLoadingSpinner ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                                    <span className="visually-hidden">Chargement...</span>
                                </div>
                                <p className="mt-3 text-muted">Chargement des clients...</p>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger d-flex align-items-center" role="alert">
                                <div className="flex-grow-1">
                                    <strong>Erreur !</strong> {error}
                                </div>
                                <Button variant="outline-danger" size="sm" onClick={fetchClients}>
                                    Réessayer
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <Table hover style={{
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                                    }}>
                                        <thead style={{
                                            backgroundColor: '#2c3e50',
                                            color: 'white'
                                        }}>
                                        <tr>
                                            <th style={{ padding: '15px', fontWeight: '600' }}>#</th>
                                            <th style={{ padding: '15px', fontWeight: '600' }}>Client</th>
                                            <th style={{ padding: '15px', fontWeight: '600' }} className="d-none d-md-table-cell">
                                                <FaEnvelope className="me-1" />
                                                Email
                                            </th>
                                            <th style={{ padding: '15px', fontWeight: '600' }} className="d-none d-lg-table-cell">
                                                <FaPhone className="me-1" />
                                                Téléphone
                                            </th>
                                            <th style={{ padding: '15px', fontWeight: '600' }} className="d-none d-md-table-cell">Statut</th>
                                            <th style={{ padding: '15px', fontWeight: '600', textAlign: 'center' }}>Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {currentClients.length > 0 ? (
                                            currentClients.map((client, index) => (
                                                <tr
                                                    key={client.id}
                                                    onClick={(e) => handleRowClick(client, e)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        borderLeft: `4px solid ${client.enabled ? '#28a745' : '#dc3545'}`
                                                    }}
                                                    className="table-row-hover"
                                                >
                                                    <td style={{ padding: '15px', fontWeight: '500', color: '#6c757d' }}>
                                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                                    </td>
                                                    <td style={{ padding: '15px' }}>
                                                        <div className="d-flex align-items-center">
                                                            <img
                                                                src={client.profilePictureUrl}
                                                                alt={`${client.firstName} ${client.lastName}`}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%',
                                                                    objectFit: 'cover',
                                                                    marginRight: '12px',
                                                                    border: '2px solid #e9ecef'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.src = DEFAULT_AVATAR;
                                                                }}
                                                            />
                                                            <div>
                                                                <strong style={{ color: '#153247' }}>
                                                                    {client.firstName} {client.lastName}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">Ref: {client.useRef}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '15px' }} className="d-none d-md-table-cell">
                                                        <div className="d-flex align-items-center">
                                                            <FaEnvelope className="me-2 text-muted" />
                                                            {truncateText(client.email, 25)}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '15px' }} className="d-none d-lg-table-cell">
                                                        <div className="d-flex align-items-center">
                                                            <FaPhone className="me-2 text-muted" />
                                                            {client.phoneNumber || (
                                                                <span className="text-muted fst-italic">Non renseigné</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '15px' }} className="d-none d-md-table-cell">
                                                        <span
                                                            className={`badge ${!client.enabled ? 'bg-success' : 'bg-danger'}`}
                                                            style={{
                                                                fontSize: '0.8rem',
                                                                padding: '6px 12px',
                                                                borderRadius: '20px'
                                                            }}
                                                        >
                                                            {!client.enabled ? (
                                                                <>
                                                                    <FaUserCheck className="me-1" />
                                                                    Actif
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FaUserTimes className="me-1" />
                                                                    Inactif
                                                                </>
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '15px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleViewDetails(client)}
                                                                style={{
                                                                    borderRadius: '6px',
                                                                    padding: '6px 12px',
                                                                    borderWidth: '2px'
                                                                }}
                                                            >
                                                                <FaEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(client)}
                                                                style={{
                                                                    borderRadius: '6px',
                                                                    padding: '6px 12px',
                                                                    borderWidth: '2px'
                                                                }}
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" style={{ padding: '40px', textAlign: 'center' }}>
                                                    <div className="text-center py-5">
                                                        <FaUser className="text-muted" style={{ fontSize: '4rem', marginBottom: '20px' }} />
                                                        <h5 className="text-muted mb-2">Aucun client trouvé</h5>
                                                        <p className="text-muted">
                                                            {debouncedSearchTerm ?
                                                                "Aucun client ne correspond à votre recherche. Essayez d'autres termes." :
                                                                "Aucun client n'est enregistré pour le moment."
                                                            }
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </Table>
                                </div>

                                {filteredClients.length > itemsPerPage && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <Pagination>
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                style={{
                                                    borderRadius: '8px',
                                                    margin: '0 2px'
                                                }}
                                            />
                                            {Array.from({ length: totalPages }, (_, i) => (
                                                <Pagination.Item
                                                    key={i + 1}
                                                    active={i + 1 === currentPage}
                                                    onClick={() => setCurrentPage(i + 1)}
                                                    style={{
                                                        borderRadius: '8px',
                                                        margin: '0 2px',
                                                        border: 'none'
                                                    }}
                                                >
                                                    {i + 1}
                                                </Pagination.Item>
                                            ))}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                style={{
                                                    borderRadius: '8px',
                                                    margin: '0 2px'
                                                }}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>

            {isModalOpen && selectedClient && (
                <ClientDetailModal
                    user={selectedClient}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveUser}
                />
            )}

            {isCreateModalOpen && (
                <CreateClientModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onCreate={handleCreateUser}
                />
            )}

            <style jsx>{`
                .table-row-hover:hover {
                    background-color: #f8f9fa !important;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                .stat-card {
                    transition: transform 0.2s ease;
                }
                .stat-card:hover {
                    transform: translateY(-5px);
                }
                .table td {
                    vertical-align: middle;
                }
            `}</style>
        </>
    );
};

export default ClientManagementPage;